import type React from 'react';

import { Avatar, Text } from '@change/ds-core-ui';
import { InsightsIcon } from '@change/ds-icons/InsightsIcon';
import { VictoryFlagIcon } from '@change/ds-icons/VictoryFlagIcon';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionMarqueeCardData } from '../../hooks/usePetitionMarquee';
import { CardWrapper } from '../CardWrapper';

type Props = Readonly<{
	petition: PetitionMarqueeCardData;
	onClick: () => void;
	duplicate?: boolean;
}>;

/** Image, title and signature count, with the petition announced as a victory or as trending. */
export const PetitionCard: React.FC<Props> = ({ petition, onClick, duplicate = false }) => {
	const { translate } = useI18n();
	const statusLabel = petition.isVictory
		? // the shared petition-card key, so the label ships translated in every locale
			translate('corgi.components.petition_card.victory')
		: translate('corgi.homepage.petition_marquee.trending_label');

	return (
		<CardWrapper cardKey={petition.key} href={petition.href} onClick={onClick} duplicate={duplicate}>
			<div className="flex grow flex-row items-center gap-3">
				<Avatar src={petition.imageUrl} alt="" aria-hidden size={72} className="shrink-0 object-cover" />
				<div className="flex min-w-0 flex-col">
					<Text as="span" variant="body" size="large" fontWeight="bold" className="line-clamp-2 pb-0.5">
						{petition.title}
					</Text>
					<span className="text-typography-weak flex items-center gap-1.5">
						{/* the icon is the only visual carrier of victory vs trending, so the status is
						    announced from text a screen reader can reach */}
						<span className="sr-only">{statusLabel}</span>
						{petition.isVictory ? (
							<VictoryFlagIcon className="text-icon-brand shrink-0" size={20} />
						) : (
							<InsightsIcon className="text-icon-brand shrink-0" size={20} />
						)}
						<Text as="span" variant="body" size="large">
							{petition.signaturesLabel}
						</Text>
					</span>
				</div>
			</div>
		</CardWrapper>
	);
};
